import React, { useEffect, useMemo, useState } from "react";
import {
    alpha,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SkeletonLoader from "@convin/components/custom_components/Loader/SkeletonLoader";
import { isDefined } from "@convin/utils/helper/common.helper";
import EmptyState from "@convin/components/custom_components/reuseable/EmptyState";
import EmptyTemplateListSvg from "@convin/components/svg/EmptyTemplateSvgList";
import EmptyParameterListSvg from "@convin/components/svg/EmptyParameterListSvg";
import {
    useGetLmsResourcesQuery,
    useGetResoursesQuery,
} from "@convin/redux/services/lms/lmsKnowledgeBase.service";
import DocumentTypeSvgg from "@convin/components/svg/FileFormats/DocumentTypeSvg";
import { useAttachLmsDocuments } from "./context/AttachLmsDocumentsContext";

interface FolderSelectionUiProps {
    open: boolean;
    onClose: () => void;
}

const FolderSelectionUi: React.FC<FolderSelectionUiProps> = ({
    open,
    onClose,
}) => {
    const { value, handleUpdate } = useAttachLmsDocuments();
    const [activeFolder, setActiveFolder] = useState<number | null>(null);

    const { data: folderList, isLoading: isFolderListLoading } =
        useGetLmsResourcesQuery(undefined, {
            skip: !open,
        });
    const { data: documents, isFetching: isLmsDocsLoading } =
        useGetResoursesQuery(
            {
                file_ext_type: [".csv", ".docx", ".md", ".pdf", ".json"],
                allow_pagination: false,
                category_id: activeFolder!,
            },
            {
                skip: !open,
            }
        );
    const [selectedFiles, setSelectedFiles] = useState<number[]>(
        value.files ?? []
    );
    const [selectedFolders, setSelectedFolders] = useState<number[]>(
        value.folders ?? []
    );
    const [folderSearchTerm, setFolderSearchTerm] = useState("");
    const [fileSearchTerm, setFileSearchTerm] = useState("");

    const handleFileChange = (fileId: number, checked: boolean) => {
        if (checked) {
            setSelectedFiles([...selectedFiles, fileId]);
        } else {
            setSelectedFiles(selectedFiles.filter((id) => id !== fileId));
        }
    };

    const handleFolderChange = (folderId: number, checked: boolean) => {
        if (checked) {
            setSelectedFolders([...selectedFolders, folderId]);
        } else {
            setSelectedFolders(selectedFolders.filter((id) => id !== folderId));
        }
    };

    const filteredFolders = useMemo(() => {
        if (!folderList?.results) return [];
        if (!folderSearchTerm.trim()) return folderList?.results;

        return folderList?.results.filter((folder) =>
            folder.name.toLowerCase().includes(folderSearchTerm.toLowerCase())
        );
    }, [folderList, folderSearchTerm]);

    // Filter foles based on search term
    const filteredFiles = useMemo(() => {
        if (!documents?.results) return [];
        const files = documents?.results.filter(
            (file) => file.category === activeFolder
        );
        if (!fileSearchTerm.trim()) return files;

        return files.filter((file) =>
            file.filename.toLowerCase().includes(fileSearchTerm.toLowerCase())
        );
    }, [documents, activeFolder, fileSearchTerm]);

    useEffect(() => {
        if (!activeFolder && folderList?.results?.length) {
            setActiveFolder(folderList?.results?.[0]?.id || null);
        }
    }, [folderList]);

    return (
        <>
            {isFolderListLoading ? (
                <SkeletonLoader />
            ) : (
                <>
                    {isDefined(folderList) && folderList?.results.length > 0 ? (
                        <>
                            <Box sx={{ display: "flex", height: "500px" }}>
                                {/* Left Panel - Template Selection */}
                                <Box
                                    sx={{
                                        width: "40%",
                                        borderRight: "1px solid #eee",
                                        p: 2,
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <TextField
                                        placeholder="Search Folder"
                                        size="small"
                                        fullWidth
                                        sx={{ mb: 2 }}
                                        value={folderSearchTerm}
                                        onChange={(e) =>
                                            setFolderSearchTerm(e.target.value)
                                        }
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <SearchIcon color="action" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                    <Stack
                                        sx={(theme) => ({
                                            overflow: "auto",
                                            flex: 1,
                                            bgcolor: alpha(
                                                theme.palette.textColors[333],
                                                0.05
                                            ),
                                        })}
                                    >
                                        {filteredFolders.map((folder) => (
                                            <Box
                                                key={folder.id}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    cursor: "pointer",
                                                    bgcolor:
                                                        activeFolder ===
                                                        folder.id
                                                            ? "#EBF3FF"
                                                            : "transparent",
                                                    "&:hover": {
                                                        bgcolor:
                                                            activeFolder ===
                                                            folder.id
                                                                ? "#EBF3FF"
                                                                : "#f5f5f5",
                                                    },
                                                    "&:not(:last-child)": {
                                                        borderBottom:
                                                            "1px solid",
                                                        borderColor: "divider",
                                                    },
                                                }}
                                                onClick={() =>
                                                    setActiveFolder(folder.id)
                                                }
                                            >
                                                <Checkbox
                                                    checked={selectedFolders.includes(
                                                        folder.id
                                                    )}
                                                    color="primary"
                                                    size="small"
                                                    onChange={(e) =>
                                                        handleFolderChange(
                                                            folder.id,
                                                            e.target.checked
                                                        )
                                                    }
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                />
                                                <Typography
                                                    variant="medium"
                                                    className="font-medium"
                                                    color={
                                                        activeFolder ===
                                                        folder.id
                                                            ? "primary.main"
                                                            : "inherit"
                                                    }
                                                >
                                                    {folder.name}
                                                </Typography>
                                                <Box sx={{ ml: "auto" }}>
                                                    <ChevronRightIcon
                                                        sx={{
                                                            fontSize: "small",
                                                            ...(activeFolder ===
                                                                folder.id && {
                                                                transform:
                                                                    "rotate(180deg)",
                                                                color: "primary.main",
                                                            }),
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>

                                <Box
                                    sx={{
                                        flex: 1,
                                        p: 2,
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <TextField
                                        placeholder="Search Files"
                                        size="small"
                                        fullWidth
                                        sx={{ mb: 2 }}
                                        value={fileSearchTerm}
                                        onChange={(e) =>
                                            setFileSearchTerm(e.target.value)
                                        }
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <SearchIcon color="action" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                    <Stack
                                        spacing={0.5}
                                        sx={{ overflow: "auto", flex: 1 }}
                                    >
                                        {isLmsDocsLoading ? (
                                            <SkeletonLoader />
                                        ) : filteredFiles.length ? (
                                            filteredFiles.map((file) => {
                                                const isDisabled =
                                                    selectedFolders.includes(
                                                        file.category
                                                    );
                                                return (
                                                    <FormControlLabel
                                                        key={file.id}
                                                        control={
                                                            !isDisabled ? (
                                                                <Checkbox
                                                                    checked={selectedFiles.includes(
                                                                        file.id
                                                                    )}
                                                                    color="primary"
                                                                    size="small"
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        handleFileChange(
                                                                            file.id,
                                                                            e
                                                                                .target
                                                                                .checked
                                                                        )
                                                                    }
                                                                />
                                                            ) : (
                                                                <></>
                                                            )
                                                        }
                                                        className="h-[40px]"
                                                        disabled={isDisabled}
                                                        label={
                                                            <Typography
                                                                variant="medium"
                                                                color={
                                                                    isDisabled
                                                                        ? "textColors.999"
                                                                        : "inherit"
                                                                }
                                                            >
                                                                <DocumentTypeSvgg />{" "}
                                                                {file.filename}
                                                            </Typography>
                                                        }
                                                    />
                                                );
                                            })
                                        ) : (
                                            <EmptyState
                                                Component={
                                                    <EmptyParameterListSvg />
                                                }
                                                text={"There are no Files"}
                                            />
                                        )}
                                    </Stack>
                                </Box>
                            </Box>

                            {/* Dialog Footer */}
                            <Box
                                sx={{
                                    p: 2,
                                    borderTop: "1px solid #eee",
                                }}
                                className="w-full flex flex-row-reverse"
                            >
                                <Box>
                                    <Button
                                        variant="outlined"
                                        sx={{ mr: 1 }}
                                        onClick={onClose}
                                        className="w-[132px]"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            handleUpdate({
                                                folders: selectedFolders,
                                                files: selectedFiles,
                                            });
                                            onClose();
                                        }}
                                        className="w-[132px]"
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <EmptyState
                            Component={<EmptyTemplateListSvg />}
                            text={"There are no folders"}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default FolderSelectionUi;
